import { useState } from 'react'
import { connect } from 'react-redux'
import { useForm } from 'react-hook-form'
import { saveRegistry } from 'registry/actions'
import { Form, Input } from 'components/forms'
import { Col, Row } from 'react-bootstrap'
import { StyledSubHeading } from 'components/headings'
import { SaveButton } from 'components/buttons'
import Link from 'components/link'
import RemoveCollaboratorModal from '../../components/modals/remove-collaborator-modal'
import { track, useTracking, withContextualizedTracking } from 'lib/analytics'
import { errorMessage, successMessage } from 'lib/flash-message'
import { REGISTRY_ROLES } from 'registry/constants'

const RegistryCollaborators = ({
  collaborators = [],
  id: registryId,
  saveRegistry,
  user,
}) => {
  const { register, handleSubmit, errors, reset } = useForm()
  const tracker = useTracking()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [removeModalData, setRemoveModalData] = useState({
    showModal: false,
    collaborator: null,
  })

  const trackCollaboratorInvited = () => {
    tracker.trackEvent({
      event: track.collaboratorInvited,
      collaboratorRole: track.CollaboratorRole.COLLABORATOR,
    })
  }

  const handleSave = async (data) => {
    const collaborator = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      role: REGISTRY_ROLES.COLLABORATOR,
    }
    setIsSubmitting(true)
    addCollaborator(collaborator)
  }

  const addCollaborator = (collaborator) => {
    const registryData = {
      registry: {
        id: registryId,
        collaborators: [...collaborators, collaborator],
      },
    }
    return saveRegistry(registryData)
      .then((resp) => {
        trackCollaboratorInvited()
        setIsSubmitting(false)
        reset()
        successMessage(
          `We've sent an invite to ${
            collaborator.firstName || collaborator.email
          }!`
        )
      })
      .catch((resp) => {
        setIsSubmitting(false)
        errorMessage()
      })
  }

  const removeCollaborator = (collaborator) => {
    const registryData = {
      registry: {
        id: registryId,
        collaborators: collaborators.filter(
          (c) => c.userId !== collaborator.userId
        ),
      },
    }
    return saveRegistry(registryData)
      .then((resp) => {
        successMessage(
          `${
            collaborator.firstName || collaborator.email || 'Your partner'
          } no longer has access to your registry.`
        )
      })
      .catch((resp) => {
        errorMessage()
      })
  }

  const sortCollaborators = () => {
    const collaboratorArr = [...collaborators]
    const partner = collaboratorArr.find(
      (c) => c.role === REGISTRY_ROLES.PARTNER
    )
    const partnerIndex = collaborators.findIndex(
      (c) => c.role === REGISTRY_ROLES.PARTNER
    )
    if (partner) {
      collaboratorArr.splice(partnerIndex, 1)
      collaboratorArr.unshift(partner)
    }
    return collaboratorArr
  }

  const validateEmail = (email) => {
    if (
      collaborators.find((c) => c.email.toLowerCase() === email.toLowerCase())
    ) {
      return 'You already have a collaborator with that email address.'
    } else if (email.toLowerCase() === user.email.toLowerCase()) {
      return "Whoops! You can't add yourself as a collaborator!"
    }
  }

  return (
    <div>
      <StyledSubHeading>Registry Collaborators</StyledSubHeading>
      <div className="h6">
        <em>
          Collaborators can add, edit and delete items from your registry.
        </em>
      </div>
      {sortCollaborators().map((c) => (
        <div key={c.email}>
          <Row>
            <Col sm={9}>
              <div>{c.firstName + ' ' + c.lastName}</div>
              <div>
                <small>{c.email}</small>
              </div>
              <div>
                <small>{c.role}</small>
                {c.state === 'pending' ? <small>{', ' + c.state}</small> : null}
              </div>
            </Col>
            <Col sm={3}>
              <Link
                className="input-group-addon--danger"
                onClick={() => {
                  setRemoveModalData({ showModal: true, collaborator: c })
                }}
              >
                Remove
              </Link>
            </Col>
          </Row>
          <br />
        </div>
      ))}
      {removeModalData.showModal ? (
        <RemoveCollaboratorModal
          show={true}
          onHide={() =>
            setRemoveModalData({ showModal: false, collaborator: null })
          }
          onSubmit={() => removeCollaborator(removeModalData.collaborator)}
          collaborator={removeModalData.collaborator}
        />
      ) : null}

      <StyledSubHeading className="h5">Invite Collaborator</StyledSubHeading>
      <Form onSubmit={handleSubmit(handleSave)}>
        <Row>
          <Col sm={6}>
            <Input
              required
              name="firstName"
              id="first_name"
              inputRef={register}
              label="Collaborator's First Name"
            />
          </Col>
          <Col sm={6}>
            <Input
              required
              name="lastName"
              id="last_name"
              inputRef={register}
              label="Collaborator's Last Name"
            />
          </Col>
        </Row>
        <Row>
          <Col sm={6}>
            <Input
              required
              name="email"
              id="email"
              inputRef={register({ validate: validateEmail })}
              label="Collaborator's Email Address"
              error={errors?.email?.message}
            />
          </Col>
        </Row>
        <SaveButton disabled={isSubmitting} pill>
          {isSubmitting ? 'Inviting...' : 'Invite'}
        </SaveButton>
      </Form>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    ...state.registry,
    user: state.user,
  }
}

export default connect(mapStateToProps, { saveRegistry })(
  withContextualizedTracking()(RegistryCollaborators)
)
